import { type RefObject, useEffect, useId, useRef, useState } from "react";
import { useCreateSkin, useSaveLook } from "../api/assetlib";
import type { AssetDetail, Dressing } from "../api/types";
import { ErrorLine } from "../ui/ErrorLine";
import { Button, Dialog, Field, Input } from "../ui";
import type { ViewerHandle } from "../viewer/Viewer";
import type { SlotDoc } from "../viewer/math";

const NAME_MAX = 60;

/** An uploaded picture's name as an artwork's: the file name without its
 *  extension. */
export function artworkName(file: string): string {
  return (
    file
      .replace(/\.[^.]*$/, "")
      .trim()
      .slice(0, NAME_MAX) || "Image"
  );
}

/** "Save this look": the object as it is dressed becomes a look of the
 *  organisation's 3D library. Each of the user's images on it becomes an
 *  artwork first; then the look is saved with a picture of the 3D view, or
 *  with the object's own picture when this browser cannot draw one. */
export function SaveLookDialog(props: {
  open: boolean;
  onClose(): void;
  shape: Pick<AssetDetail, "id" | "meta">;
  dressing: Dressing;
  viewerRef: RefObject<ViewerHandle>;
  onSaved(id: string): void;
}): JSX.Element {
  const { open, shape, dressing, viewerRef } = props;
  const formId = useId();
  const createSkin = useCreateSkin();
  const saveLook = useSaveLook();
  const suggested = `${String(shape.meta.name ?? "")} (my look)`.slice(
    0,
    NAME_MAX,
  );
  const [name, setName] = useState(suggested);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // upload id -> artwork id: a retry after a later step failed does not make
  // the same artwork twice.
  const made = useRef(new Map<string, string>());

  // Each opening starts from the suggested name and no error.
  useEffect(() => {
    if (!open) return;
    setName(suggested);
    setError(null);
  }, [open, suggested]);

  const save = async () => {
    const typed = name.trim();
    if (!typed || saving) return;
    setSaving(true);
    setError(null);
    try {
      const slots = (shape.meta.slots ?? []) as SlotDoc[];
      const skins: Record<string, string> = {};
      for (const [slot, value] of Object.entries(dressing.skins)) {
        if (typeof value === "string") {
          skins[slot] = value;
          continue;
        }
        let id = made.current.get(value.upload_id);
        if (id === undefined) {
          const doc = slots.find((s) => s.name === slot);
          const saved = await createSkin.mutateAsync({
            name: artworkName(value.name),
            upload_id: value.upload_id,
            region_type: doc?.region?.type ?? "flat",
            emissive: doc?.accepts.includes("screen") ?? false,
            ...(doc?.region ? { fit: { aspect: doc.region.aspect } } : {}),
          });
          id = saved.id;
          made.current.set(value.upload_id, id);
        }
        skins[slot] = id;
      }
      let blob: Blob | null = null;
      try {
        blob = (await viewerRef.current?.snapshot()) ?? null;
      } catch {
        // No 3D in this browser: the server uses the object's own picture.
      }
      const form = new FormData();
      form.append(
        "document",
        JSON.stringify({
          name: typed,
          shape: shape.id,
          materials: dressing.materials,
          skins,
          colors_hex: dressing.colors_hex,
        }),
      );
      if (blob) {
        form.append(
          "thumbnail",
          new File([blob], "thumb.jpg", { type: "image/jpeg" }),
        );
      }
      const saved = await saveLook.mutateAsync(form);
      props.onSaved(saved.id);
    } catch (failure) {
      setError(failure);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!saving) props.onClose();
      }}
      title="Save this look"
      footer={
        <>
          <Button
            type="button"
            variant="ghost"
            disabled={saving}
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            className="disabled:cursor-not-allowed disabled:opacity-50"
            disabled={saving || !name.trim()}
            aria-busy={saving}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <Field label="Name">
          <Input
            required
            maxLength={NAME_MAX}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        {error ? <ErrorLine error={error} context="plain" /> : null}
      </form>
    </Dialog>
  );
}
